import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Download,
  ExternalLink,
  Filter,
  LayoutGrid,
  LineChart as LineChartIcon,
  List,
  Loader2,
  Search,
  Table2,
} from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/components/ui/use-toast';
import { TrainingService } from '@/services/TrainingService';
import { supabase } from '@/integrations/supabase/client';
import { getSelfServeAddonById } from '@/lib/marketing';
import { selfServeTiers } from '@/lib/marketing';
import { type TrainingJob } from '@/types/training';
import HostingApiKeyDialog from './HostingApiKeyDialog';
import JobDetailDialog from './JobDetailDialog';
import JobStatusBadge from './JobStatusBadge';
import MetricsDrawer from './MetricsDrawer';

type Props = {
  userId: string | null;
};

const PAGE_SIZE = 20;

type WorkflowFilter = 'all' | 'fine-tuning' | 'pre-training' | 'frontier';
type ViewMode = 'tiles' | 'list' | 'details';

function getPackageId(job: TrainingJob): string | null {
  const spec = job.spec as Record<string, unknown> | null;
  const packageId = spec?.package_id;
  return typeof packageId === 'string' ? packageId : null;
}

function getWorkflowFilter(job: TrainingJob): WorkflowFilter {
  const packageId = getPackageId(job);
  if (packageId === 'frontier') return 'frontier';
  if (packageId === 'pre-training' || job.training_type === 'cpt') return 'pre-training';
  return 'fine-tuning';
}

function getWorkflowLabel(job: TrainingJob) {
  const workflow = getWorkflowFilter(job);
  if (workflow === 'pre-training') return 'Pre-Training';
  if (workflow === 'frontier') return 'Frontier';
  return 'Fine-Tune';
}

function getWorkflowBadgeClasses(workflow: WorkflowFilter) {
  if (workflow === 'frontier') return 'border-canada-red/40 bg-canada-red/10 text-canada-red';
  if (workflow === 'pre-training') return 'border-amber-500/30 bg-amber-500/10 text-amber-300';
  return 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300';
}

function formatBaseModel(baseModel: string) {
  return baseModel.split('/').pop() || baseModel;
}

function formatCreatedAt(value: string) {
  return new Date(value).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getTierName(job: TrainingJob) {
  const packageId = getPackageId(job);
  return packageId
    ? selfServeTiers.find((tier) => tier.id === packageId)?.name || getWorkflowLabel(job)
    : getWorkflowLabel(job);
}

function getUserHuggingFaceUrl(job: TrainingJob) {
  const metrics = (job.metrics as Record<string, any> | null) || null;
  return typeof metrics?.huggingface_user?.url === 'string' ? metrics.huggingface_user.url : null;
}

// Hardcoded expected wall-clock per stage. Mostly derived from
// observed RunPod cold-pull / model-load times. Used to compute the
// rough ETA shown to the user during chat warmup. Soft cap: when
// elapsed exceeds the expected duration we switch to a "still
// working" message so we never lie about ETA.
const STAGE_EXPECTED_SECONDS: Record<string, number> = {
  starting: 60,        // RunPod pod allocation + container boot
  pulling_image: 600,  // 18 GB inference image cold-pull
  loading_model: 240,  // Weights into GPU memory
};

function formatTimer(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatEta(seconds: number): string {
  if (seconds < 60) return `~${Math.max(10, Math.ceil(seconds / 10) * 10)}s left`;
  return `~${Math.ceil(seconds / 60)} min left`;
}

function getChatPreparationLabel(result: {
  status: 'starting' | 'pulling_image' | 'loading_model' | 'ready' | 'failed';
  message: string;
  elapsed_seconds?: number;
}) {
  if (result.status === 'ready') return 'Opening chat…';
  if (result.status === 'failed') return result.message || 'Chat unavailable';

  const stageLabel = {
    starting: 'Warming up',
    pulling_image: 'Preparing environment',
    loading_model: 'Loading model',
  }[result.status];

  const elapsed = Math.max(0, Math.floor(result.elapsed_seconds ?? 0));
  const expected = STAGE_EXPECTED_SECONDS[result.status] ?? 0;
  const remaining = expected - elapsed;
  const elapsedStr = formatTimer(elapsed);

  if (expected === 0 || remaining <= 5) {
    // Past our ETA — be honest rather than lie about "0 min left"
    return `${stageLabel} — ${elapsedStr} elapsed, still working…`;
  }
  return `${stageLabel} — ${elapsedStr}, ${formatEta(remaining)}`;
}

export default function TrainedModelsSection({ userId }: Props) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<TrainingJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [workflowFilter, setWorkflowFilter] = useState<WorkflowFilter>('all');
  const [baseModelFilter, setBaseModelFilter] = useState('all');
  const [viewMode, setViewMode] = useState<ViewMode>('details');
  const [page, setPage] = useState(1);
  const [openJobId, setOpenJobId] = useState<string | null>(null);

  const [openMetricsJobId, setOpenMetricsJobId] = useState<string | null>(null);
  const [preparingJobId, setPreparingJobId] = useState<string | null>(null);
  const [preparingMessage, setPreparingMessage] = useState('Preparing chat…');
  const [hostingJobId, setHostingJobId] = useState<string | null>(null);
  // When set, opens HostingApiKeyDialog. apiKey may be undefined if
  // the user re-opens the dialog on an already-hosted job (we can't
  // retrieve the original plaintext — they have to rotate).
  const [apiKeyDialog, setApiKeyDialog] = useState<{
    jobId: string;
    outputName: string;
    apiKey: string;
    baseUrl: string;
  } | null>(null);

  const loadJobs = useCallback(async () => {
    if (!userId) {
      setJobs([]);
      return;
    }

    setLoading(true);
    try {
      const list = await TrainingService.listJobs({ limit: 200 });
      const completedJobs = list.jobs
        .filter((job) => job.status === 'completed')
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setJobs(completedJobs);
    } catch (e: any) {
      toast({
        title: 'Could not load trained models',
        description: String(e?.message || e),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast, userId]);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  const baseModelOptions = useMemo(
    () => Array.from(new Set(jobs.map((job) => job.base_model))).sort((a, b) => a.localeCompare(b)),
    [jobs],
  );

  const filteredJobs = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return jobs.filter((job) => {
      const workflow = getWorkflowFilter(job);
      const matchesSearch = !normalizedSearch
        || job.output_name.toLowerCase().includes(normalizedSearch)
        || job.base_model.toLowerCase().includes(normalizedSearch)
        || getWorkflowLabel(job).toLowerCase().includes(normalizedSearch);
      const matchesWorkflow = workflowFilter === 'all' || workflow === workflowFilter;
      const matchesBaseModel = baseModelFilter === 'all' || job.base_model === baseModelFilter;
      return matchesSearch && matchesWorkflow && matchesBaseModel;
    });
  }, [baseModelFilter, jobs, searchTerm, workflowFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [searchTerm, workflowFilter, baseModelFilter, viewMode]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedJobs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredJobs.slice(start, start + PAGE_SIZE);
  }, [filteredJobs, page]);

  const metricsJob = useMemo(() => jobs.find((job) => job.id === openMetricsJobId) ?? null, [jobs, openMetricsJobId]);

  const handleHost = useCallback(
    async (job: TrainingJob) => {
      if (hostingJobId) return;

      // Clicking "Hosted ✓" toggles off — what the button affordance
      // promises. (Earlier I had this open the API-key dialog in
      // rotate mode; that contradicted the visual cue. The API key
      // dialog is now reached via a separate path / on first host.)
      if (job.is_hosted) {
        setHostingJobId(job.id);
        try {
          const result = await TrainingService.setHosted(job.id, false);
          setJobs((prev) =>
            prev.map((j) =>
              j.id === job.id ? { ...j, is_hosted: result.is_hosted } : j,
            ),
          );
          toast({
            title: 'Hosting disabled',
            description: 'Model is no longer kept warm. Cold-start delay returns on the next chat.',
          });
        } catch (e: any) {
          toast({
            title: 'Could not disable hosting',
            description: String(e?.message || e),
            variant: 'destructive',
          });
        } finally {
          setHostingJobId(null);
        }
        return;
      }

      setHostingJobId(job.id);
      try {
        const result = await TrainingService.setHosted(job.id, true);
        setJobs((prev) =>
          prev.map((j) =>
            j.id === job.id
              ? { ...j, is_hosted: result.is_hosted, hosted_at: new Date().toISOString() }
              : j,
          ),
        );

        // Auto-issue an API key so the dialog opens with the plaintext
        // ready to copy. Issuance shares the same gate as Host so this
        // should never 402 right after a successful enable.
        try {
          const keyResult = await TrainingService.issueApiKey(job.id);
          setApiKeyDialog({
            jobId: job.id,
            outputName: job.output_name,
            apiKey: keyResult.api_key,
            baseUrl: keyResult.base_url,
          });
          toast({
            title: 'Hosting enabled',
            description: result._gate === 'admin'
              ? 'Admin test — no charge applied. API key generated below.'
              : 'Subscription active. API key generated below.',
          });
        } catch (keyErr: any) {
          toast({
            title: 'Hosting enabled',
            description: `Hosted, but API key issuance failed: ${String(keyErr?.message || keyErr)}.`,
            variant: 'destructive',
          });
        }
      } catch (e: any) {
        if (e?.checkoutRequired) {
          const addon = getSelfServeAddonById(e.addonId || 'dedicated-inference-endpoint');
          toast({
            title: 'Subscription required',
            description: `${addon?.name || 'Dedicated Inference Endpoint'} (${addon?.price || '$29/month CAD'}) — opening Stripe Checkout…`,
          });
          // Kick the user into Stripe Checkout for the recurring addon.
          // After they pay, the stripe-cart-webhook writes a row to
          // user_subscriptions(plan_id='dedicated-inference-endpoint'),
          // and the next click on Host will sail through.
          try {
            const returnUrl = `${window.location.origin}/jobs?hosting_for=${encodeURIComponent(job.id)}`;
            const { data, error } = await supabase.functions.invoke('create-addon-checkout', {
              body: {
                addonItems: [{ id: 'dedicated-inference-endpoint', quantity: 1 }],
                returnUrl,
              },
            });
            if (error || !data?.url) {
              throw new Error(data?.error || error?.message || 'Checkout creation failed');
            }
            window.location.assign(data.url);
            return;
          } catch (ce: any) {
            toast({
              title: 'Could not open checkout',
              description: String(ce?.message || ce),
              variant: 'destructive',
            });
          }
        } else {
          toast({
            title: enable ? 'Could not host' : 'Could not unhost',
            description: String(e?.message || e),
            variant: 'destructive',
          });
        }
      } finally {
        setHostingJobId(null);
      }
    },
    [hostingJobId, toast],
  );

  const handleChat = useCallback(
    async (jobId: string) => {
      if (preparingJobId) return;

      setPreparingJobId(jobId);
      setPreparingMessage('Starting chat…');

      try {
        const deadlineMs = Date.now() + 20 * 60 * 1000;

        while (true) {
          const result = await TrainingService.prepareChat(jobId);
          setPreparingMessage(getChatPreparationLabel(result));

          if (result.status === 'ready') {
            navigate(`/finetune-chat/${jobId}`);
            return;
          }

          if (result.status === 'failed') {
            throw new Error(result.message || 'Could not prepare chat for this model.');
          }

          if (Date.now() > deadlineMs) {
            throw new Error('Model is still warming up. Please try again in a minute.');
          }

          await new Promise((resolve) => window.setTimeout(resolve, result.status === 'pulling_image' ? 4000 : 2500));
        }
      } catch (e: any) {
        toast({
          title: 'Could not open chat',
          description: String(e?.message || e),
          variant: 'destructive',
        });
      } finally {
        setPreparingJobId(null);
        setPreparingMessage('Preparing chat…');
      }
    },
    [navigate, preparingJobId, toast],
  );

  const renderWorkflowBadge = (job: TrainingJob) => {
    const workflow = getWorkflowFilter(job);
    return (
      <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${getWorkflowBadgeClasses(workflow)}`}>
        {getWorkflowLabel(job)}
      </span>
    );
  };

  const renderModelActions = (job: TrainingJob, compact = false) => {
    const userHfUrl = getUserHuggingFaceUrl(job);

    return (
      <div className={compact ? 'flex flex-wrap items-center gap-2' : 'space-y-2'}>
        <Button
          variant="destructive"
          size={compact ? 'sm' : 'default'}
          className={compact ? '' : 'w-full'}
          onClick={(event) => {
            event.stopPropagation();
            void handleChat(job.id);
          }}
          disabled={preparingJobId !== null}
        >
          {preparingJobId === job.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          <span className="truncate">{preparingJobId === job.id ? preparingMessage : 'Chat'}</span>
        </Button>

        <Button
          variant="outline"
          size={compact ? 'sm' : 'default'}
          className={compact ? '' : 'w-full'}
          onClick={(event) => {
            event.stopPropagation();
            setOpenMetricsJobId(job.id);
          }}
        >
          <LineChartIcon className="h-4 w-4" />
          Charts
        </Button>

        <Button
          variant={job.is_hosted ? 'default' : 'outline'}
          size={compact ? 'sm' : 'default'}
          className={compact ? '' : 'w-full'}
          onClick={(event) => {
            event.stopPropagation();
            void handleHost(job);
          }}
          disabled={hostingJobId !== null}
          title={
            job.is_hosted
              ? 'Click to disable hosting. Cold starts will apply on next chat.'
              : 'Keep this model warm — no cold-start delay. $29/month CAD (free for admins).'
          }
        >
          {hostingJobId === job.id ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {job.is_hosted ? 'Hosted ✓' : 'Host'}
        </Button>

        <Button
          variant="outline"
          size={compact ? 'sm' : 'default'}
          className={compact ? '' : 'w-full'}
          onClick={(event) => {
            event.stopPropagation();
            setOpenJobId(job.id);
          }}
        >
          Details
        </Button>

        {userHfUrl ? (
          <a
            href={userHfUrl}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            className={`inline-flex items-center justify-center gap-2 rounded-md border border-canada-red/30 bg-canada-red/5 px-3 py-2 text-sm font-medium text-canada-red transition hover:bg-canada-red/10 ${compact ? '' : 'w-full'}`}
          >
            <ExternalLink className="h-4 w-4" />
            HF
          </a>
        ) : null}

        <a
          href={`/api/training/jobs/${encodeURIComponent(job.id)}/adapter/download`}
          download
          onClick={(event) => event.stopPropagation()}
          className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition hover:bg-muted/40 ${compact ? '' : 'w-full'}`}
        >
          <Download className="h-4 w-4" />
          Adapter
        </a>
      </div>
    );
  };

  const renderTileView = () => (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {paginatedJobs.map((job) => (
        <Card
          key={job.id}
          className="group flex h-full flex-col border-border/70 bg-gradient-to-br from-background via-background to-canada-red/[0.03] transition hover:border-canada-red/30 hover:shadow-lg hover:shadow-canada-red/5"
        >
          <CardHeader className="space-y-3 pb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <CardTitle className="line-clamp-2 text-base leading-snug">{job.output_name}</CardTitle>
                <CardDescription className="mt-1">
                  {formatCreatedAt(job.created_at)}
                </CardDescription>
              </div>
              {renderWorkflowBadge(job)}
            </div>

            <div className="grid gap-2 text-xs text-muted-foreground">
              <div className="rounded-lg border border-border/70 bg-background/50 px-3 py-2">
                <span className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground/80">Base model</span>
                <span className="mt-1 block truncate text-sm text-foreground">{formatBaseModel(job.base_model)}</span>
              </div>
              <div className="rounded-lg border border-border/70 bg-background/50 px-3 py-2">
                <span className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground/80">Training package</span>
                <span className="mt-1 block truncate text-sm text-foreground">{getTierName(job)}</span>
              </div>
              <div className="rounded-lg border border-border/70 bg-background/50 px-3 py-2">
                <span className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground/80">Method</span>
                <span className="mt-1 block text-sm uppercase text-foreground">{job.training_type}</span>
              </div>
            </div>
          </CardHeader>

          <CardContent className="mt-auto pt-0">
            {renderModelActions(job)}
          </CardContent>
        </Card>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="space-y-2">
      {paginatedJobs.map((job) => (
        <button
          type="button"
          key={job.id}
          onClick={() => setOpenJobId(job.id)}
          className="w-full rounded-xl border border-border/70 bg-background/70 p-4 text-left transition hover:border-canada-red/30 hover:bg-muted/30"
        >
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="max-w-xl truncate font-medium text-foreground">{job.output_name}</span>
                {renderWorkflowBadge(job)}
                <JobStatusBadge status={job.status} />
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                <span>Base: {formatBaseModel(job.base_model)}</span>
                <span>Method: {job.training_type.toUpperCase()}</span>
                <span>Package: {getTierName(job)}</span>
                <span>Created: {formatCreatedAt(job.created_at)}</span>
              </div>
            </div>
            <div className="shrink-0" onClick={(event) => event.stopPropagation()}>
              {renderModelActions(job, true)}
            </div>
          </div>
        </button>
      ))}
    </div>
  );

  const renderDetailsView = () => (
    <div className="overflow-hidden rounded-xl border border-border/70 bg-background/70">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30 hover:bg-muted/30">
            <TableHead className="h-9 min-w-[220px] px-3 text-xs">Name</TableHead>
            <TableHead className="h-9 min-w-[130px] px-3 text-xs">Base</TableHead>
            <TableHead className="h-9 w-[88px] px-3 text-xs">Method</TableHead>
            <TableHead className="hidden h-9 min-w-[150px] px-3 text-xs 2xl:table-cell">Package</TableHead>
            <TableHead className="hidden h-9 min-w-[145px] px-3 text-xs lg:table-cell">Created</TableHead>
            <TableHead className="h-9 min-w-[230px] px-3 text-right text-xs">Actions</TableHead>
          </TableRow>

        </TableHeader>

        <TableBody>
          {paginatedJobs.map((job) => {
            const userHfUrl = getUserHuggingFaceUrl(job);

            return (
              <TableRow
                key={job.id}
                onClick={() => setOpenJobId(job.id)}
                className="cursor-pointer"
              >
                <TableCell className="px-3 py-2">
                  <div className="max-w-[300px] truncate text-sm font-medium text-foreground" title={job.output_name}>
                    {job.output_name}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                    {renderWorkflowBadge(job)}
                    {job.is_hosted ? (
                      <span className="shrink-0 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-500">
                        Hosted
                      </span>
                    ) : null}
                  </div>
                </TableCell>
                <TableCell className="max-w-[180px] truncate px-3 py-2 text-sm" title={job.base_model}>{formatBaseModel(job.base_model)}</TableCell>
                <TableCell className="px-3 py-2 text-xs uppercase text-muted-foreground">{job.training_type}</TableCell>
                <TableCell className="hidden max-w-[190px] truncate px-3 py-2 text-sm 2xl:table-cell" title={getTierName(job)}>{getTierName(job)}</TableCell>

                <TableCell className="hidden px-3 py-2 text-sm text-muted-foreground lg:table-cell">{formatCreatedAt(job.created_at)}</TableCell>

                <TableCell className="px-3 py-2 text-right" onClick={(event) => event.stopPropagation()}>
                  <div className="flex flex-wrap justify-end gap-1.5">
                    <Button
                      variant="destructive"
                      size="sm"
                      className="h-8 px-3 text-xs"
                      onClick={() => void handleChat(job.id)}
                      disabled={preparingJobId !== null}
                    >
                      {preparingJobId === job.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                      {preparingJobId === job.id ? preparingMessage : 'Chat'}
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 px-2.5 text-xs" onClick={() => setOpenMetricsJobId(job.id)}>
                      <LineChartIcon className="h-3.5 w-3.5" />
                      Charts
                    </Button>
                    <Button
                      variant={job.is_hosted ? 'default' : 'outline'}
                      size="sm"
                      className="h-8 px-2.5 text-xs"
                      onClick={() => void handleHost(job)}
                      disabled={hostingJobId !== null}
                      title={
                        job.is_hosted
                          ? 'Hosted — your model stays warm. Click to disable.'
                          : 'Host this model ($29/month CAD, free for admins) — no cold-start delay.'
                      }
                    >
                      {hostingJobId === job.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                      {job.is_hosted ? 'Hosted ✓' : 'Host'}
                    </Button>
                    <Button variant="outline" size="sm" className="h-8 px-2.5 text-xs" onClick={() => setOpenJobId(job.id)}>
                      Details
                    </Button>
                    <a
                      href={`/api/training/jobs/${encodeURIComponent(job.id)}/adapter/download`}
                      download
                      className="hidden h-8 items-center justify-center gap-1.5 rounded-md border px-2.5 text-xs font-medium transition hover:bg-muted/40 lg:inline-flex"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Adapter
                    </a>
                    {userHfUrl ? (
                      <a
                        href={userHfUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="hidden h-8 items-center justify-center gap-1.5 rounded-md border border-canada-red/30 bg-canada-red/5 px-2.5 text-xs font-medium text-canada-red transition hover:bg-canada-red/10 xl:inline-flex"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        HF
                      </a>
                    ) : null}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );

  return (

    <section className="space-y-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Your Trained models</h2>
          <p className="text-sm text-muted-foreground">
            Browse the models you’ve trained so far, newest first.
          </p>
        </div>
        <Button variant="outline" onClick={() => void loadJobs()} disabled={loading || !userId}>
          Refresh models
        </Button>
      </div>

      <Card className="border-border/70 bg-background/70">
        <CardHeader className="space-y-4">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Filter className="h-4 w-4 text-canada-red" />
              Search and filter
            </CardTitle>
            <CardDescription>
              Search by model name and narrow by workflow or base model.
            </CardDescription>
          </div>

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1.4fr)_220px_260px]">
            <div className="space-y-2">
              <Label htmlFor="trained-model-search">Search</Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="trained-model-search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search by trained model name or base model"
                  className="pl-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Workflow</Label>
              <Select value={workflowFilter} onValueChange={(value) => setWorkflowFilter(value as WorkflowFilter)}>
                <SelectTrigger>
                  <SelectValue placeholder="All workflows" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All workflows</SelectItem>
                  <SelectItem value="fine-tuning">Fine-Tune</SelectItem>
                  <SelectItem value="pre-training">Pre-Training</SelectItem>
                  <SelectItem value="frontier">Frontier</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Base model</Label>
              <Select value={baseModelFilter} onValueChange={setBaseModelFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All base models" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All base models</SelectItem>
                  {baseModelOptions.map((baseModel) => (
                    <SelectItem key={baseModel} value={baseModel}>
                      {formatBaseModel(baseModel)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          <div className="flex flex-col gap-3 text-sm text-muted-foreground xl:flex-row xl:items-center xl:justify-between">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
              <span>
                {loading ? 'Loading models…' : `${filteredJobs.length} trained model${filteredJobs.length === 1 ? '' : 's'} found`}
              </span>
              {!loading && filteredJobs.length > 0 ? (
                <span>
                  Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filteredJobs.length)} of {filteredJobs.length}
                </span>
              ) : null}
            </div>

            <div className="inline-flex w-fit overflow-hidden rounded-lg border border-border/70 bg-background p-1 shadow-sm">
              <Button
                type="button"
                variant={viewMode === 'tiles' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 gap-1.5"
                onClick={() => setViewMode('tiles')}
                title="Tiles view"
              >
                <LayoutGrid className="h-4 w-4" />
                Tiles
              </Button>
              <Button
                type="button"
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 gap-1.5"
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <List className="h-4 w-4" />
                List
              </Button>
              <Button
                type="button"
                variant={viewMode === 'details' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 gap-1.5"
                onClick={() => setViewMode('details')}
                title="Details view"
              >
                <Table2 className="h-4 w-4" />
                Details
              </Button>
            </div>
          </div>

          {loading ? (
            <div className={viewMode === 'tiles' ? 'grid gap-4 md:grid-cols-2 xl:grid-cols-4' : 'space-y-2'}>
              {Array.from({ length: viewMode === 'tiles' ? 4 : 8 }).map((_, index) => (
                <div key={index} className={viewMode === 'tiles' ? 'h-56 rounded-2xl border border-border/70 bg-muted/20' : 'h-16 rounded-xl border border-border/70 bg-muted/20'} />
              ))}
            </div>
          ) : paginatedJobs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border/70 p-10 text-center text-sm text-muted-foreground">
              {jobs.length === 0
                ? 'No trained models yet. Completed training runs will appear here.'
                : 'No models match your current search and filters.'}
            </div>
          ) : (
            <>
              {viewMode === 'tiles' ? renderTileView() : viewMode === 'list' ? renderListView() : renderDetailsView()}

              {totalPages > 1 ? (
                <div className="flex flex-col gap-3 border-t border-border/70 pt-5 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page === 1}>
                      Previous
                    </Button>
                    <Button variant="outline" onClick={() => setPage((current) => Math.min(totalPages, current + 1))} disabled={page === totalPages}>
                      Next
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
      </Card>

      <JobDetailDialog
        open={!!openJobId}
        onOpenChange={(open) => {
          if (!open) {
            setOpenJobId(null);
          }
        }}
        jobId={openJobId}
      />

      <MetricsDrawer
        open={!!openMetricsJobId}
        onOpenChange={(open) => {
          if (!open) {
            setOpenMetricsJobId(null);
          }
        }}
        job={metricsJob}
      />

      {apiKeyDialog ? (
        <HostingApiKeyDialog
          open={!!apiKeyDialog}
          onOpenChange={(open) => {
            if (!open) setApiKeyDialog(null);
          }}
          jobId={apiKeyDialog.jobId}
          outputName={apiKeyDialog.outputName}
          apiKey={apiKeyDialog.apiKey}
          baseUrl={apiKeyDialog.baseUrl}
          onRotated={(next) => {
            setApiKeyDialog((prev) =>
              prev ? { ...prev, apiKey: next.apiKey, baseUrl: next.baseUrl } : prev,
            );
          }}
        />
      ) : null}
    </section>
  );
}
